import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useListCopy } from 'app/hooks/useListCopy'
import { usePipelineStore } from 'app/stores/StoreContext'
import { useUser } from 'app/utils'
import { pipelineOperations } from 'app/lib/workflows/actionCatalogs'
import type { AddToPipelineNodeData, PipelineOperation } from 'app/types/workflows'
import { type Panel } from './panelShared'
import { ChoiceSelect, Field } from './fields'
import { Section } from './Section'

export const AddToPipelinePanel = observer(function AddToPipelinePanel({
  data,
  set,
  errors,
}: Panel<AddToPipelineNodeData>) {
  const pipelineStore = usePipelineStore()
  useEffect(() => {
    if (!pipelineStore.hits.length) pipelineStore.fetchList()
  }, [pipelineStore])
  // assignableHits, not hits: this node runs unattended and would write
  // system-pipeline memberships no board's search will ever surface.
  //
  // Super users get the unfiltered set. The Action Center's queues are filled
  // by workflows that write exactly those memberships, and somebody has to be
  // able to author them; a practice still cannot, which is the case the filter
  // was put here for.
  const user = useUser()
  const copy = useListCopy()
  const pipelines = user?.type === `super` ? pipelineStore.hits : pipelineStore.assignableHits
  const selected = pipelines.find((p) => p.id === data.pipelineId)
  const stages = (selected?.stages ?? []).map((stage) => stage.name)
  const isMove = data.operation === `move`
  return (
    <>
      <Section>
        <Field label="Action">
          <ChoiceSelect
            value={data.operation}
            choices={pipelineOperations(copy)}
            onChange={(v) => set({ operation: v as PipelineOperation })}
          />
        </Field>
        <Field
          label={copy.Noun}
          error={errors.pipelineId}
          // Never "the pipeline the contact is already in": the engine upserts, so that stated a
          // precondition nothing checks (TRK-5674). The stage field carries the consequence.
          info={isMove ? copy.listToMoveWithin : undefined}
          // Only the list wording has something to say here: a filter list has no one to add.
          hint={copy.filterListsHelper}
        >
          <ChoiceSelect
            value={data.pipelineId ?? ``}
            choices={pipelines.map((p) => ({ value: p.id, label: p.name }))}
            placeholder={pipelines.length ? copy.selectAList : `Loading…`}
            invalid={!!errors.pipelineId}
            onChange={(v) =>
              set({
                pipelineId: v || undefined,
                pipelineName: pipelines.find((p) => p.id === v)?.name,
                stage: undefined,
              })
            }
          />
        </Field>
        {data.pipelineId && (
          <Field
            label={isMove ? `Move to stage` : `Stage`}
            error={errors.stage}
            // A consequence, so it stays on screen: Move also adds anyone not yet in the pipeline.
            hint={isMove ? copy.notInThisListYet : undefined}
          >
            <ChoiceSelect
              value={data.stage ?? ``}
              choices={
                isMove
                  ? stages.map((stage) => ({ value: stage, label: stage }))
                  : [{ value: ``, label: `First stage` }, ...stages.map((stage) => ({ value: stage, label: stage }))]
              }
              placeholder={isMove ? `Select a stage…` : undefined}
              invalid={!!errors.stage}
              onChange={(v) => set({ stage: v || undefined })}
            />
          </Field>
        )}
      </Section>
    </>
  )
})
